/**
 * QuickStartScreen1 - BGG ID Import
 *
 * First screen of the Quick Start onboarding flow.
 * Allows users to enter their BGG ID and import their collection.
 * Includes opt-out option for users without BGG accounts.
 */

import React from "react";

const QuickStartScreen1 = ({
  bggId,
  setBggId,
  isImporting,
  importProgress,
  importResult,
  onSubmit,
  onOptOut,
  onSkip,
  username,
  setUsername,
  isSavingUsername,
  usernameError,
  onUsernameSave,
}) => {
  const handleSubmit = (e) => {
    e.preventDefault();
    if (bggId.trim()) {
      onSubmit(bggId.trim());
    }
  };

  return (
    <div className="bgg-screen">
      <div className="bgg-input-group" style={{ maxWidth: 400, margin: "0 auto" }}>
        <label htmlFor="quick-start-username">Your Username</label>
        <input
          id="quick-start-username"
          type="text"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          onBlur={(e) => onUsernameSave(e.target.value)}
          placeholder="Choose a username"
          className="bgg-input"
          disabled={isSavingUsername}
        />
        <span className="bgg-help">
          This is how other users will see you identified in the application.
        </span>
        {usernameError && (
          <span className="bgg-help" style={{ color: "var(--error-color, #c62828)" }}>
            {usernameError}
          </span>
        )}
      </div>

      <div className="bgg-header">
        <h3>Connect Your BoardGameGeek Account</h3>
        <p>
          Import your game collection from BoardGameGeek to get personalized recommendations.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="bgg-form">
        <div className="bgg-input-group">
          <label htmlFor="bgg-username">BoardGameGeek Username</label>
          <input
            id="bgg-username"
            type="text"
            value={bggId}
            onChange={(e) => setBggId(e.target.value)}
            placeholder="Enter your BGG username"
            className="bgg-input"
            disabled={isImporting}
            autoFocus
          />
          <span className="bgg-help">
            Your BGG username is case-sensitive. You can find it on your{" "}
            <a
              href="https://boardgamegeek.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              BoardGameGeek profile
            </a>
            .
          </span>
        </div>

        <button
          type="submit"
          className="btn btn-primary"
          disabled={!bggId.trim() || isImporting}
        >
          {isImporting ? (
            <>
              <span className="loading-spinner" style={{ width: 16, height: 16, borderWidth: 2 }} />
              Importing Collection...
            </>
          ) : (
            "Save & Import Collection"
          )}
        </button>

        {/* Progress bar during import */}
        {isImporting && (
          <div className="import-progress-container" style={{ marginTop: "1rem" }}>
            <div
              className="import-progress-bar"
              style={{
                width: "100%",
                height: "8px",
                backgroundColor: "var(--border-color, #e0e0e0)",
                borderRadius: "4px",
                overflow: "hidden",
              }}
            >
              <div
                className="import-progress-fill"
                style={{
                  width: `${importProgress || 0}%`,
                  height: "100%",
                  backgroundColor: "var(--primary-color, #4a90d9)",
                  transition: "width 0.3s ease",
                }}
              />
            </div>
            <div
              className="import-progress-text"
              style={{
                marginTop: "0.5rem",
                fontSize: "0.875rem",
                color: "var(--text-secondary, #666)",
                textAlign: "center",
              }}
            >
              {importProgress ? `${Math.round(importProgress)}% - Processing your collection...` : "Fetching from BoardGameGeek..."}
            </div>
          </div>
        )}
      </form>

      {importResult && (
        <div className={`bgg-result ${importResult.success ? "success" : "error"}`}>
          {importResult.success ? (
            <>
              Successfully imported {importResult.imported_count || importResult.total_games || "your"} games!
              <br />
              Proceeding to next step...
            </>
          ) : (
            <>
              Import failed: {importResult.error}
              <br />
              You can try again or skip this step.
            </>
          )}
        </div>
      )}

      <div className="bgg-opt-out">
        <button
          type="button"
          className="btn btn-text"
          onClick={onOptOut}
          disabled={isImporting}
        >
          I don&apos;t have a BoardGameGeek account
        </button>
      </div>

      <div style={{ textAlign: "center", marginTop: "1rem" }}>
        <button
          type="button"
          className="btn btn-text"
          onClick={onSkip}
          disabled={isImporting}
        >
          Skip this step →
        </button>
      </div>
    </div>
  );
};

export default QuickStartScreen1;
